import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { toast } from "react-toastify";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import Icon from "../../../components/Icon";
import Input from "../../../components/Input";
import InputTextarea from "../../../components/InputTextarea";
import InputIcon from "../../../components/InputIcon";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal";
import { Loading } from "../../../components/Loading";
import HomePreview, { ProductPreview } from "./HomePreview";
import {
    SHARED_KEY,
    countBlockChanges,
    diffThemeContent,
    getEmptyItem,
    getBlockDefaults,
    getThemeBlocks,
    getThemeSchema,
    isThemeEditable,
    mergeThemeContent
} from "../../../helpers/siteContent";

const clamp = (value, maxLength) => maxLength ? (value ?? '').slice(0, maxLength) : value;

const SiteContent = forwardRef(({ themeSlug }, ref) => {
    const editable = isThemeEditable(themeSlug);
    const schema = getThemeSchema(themeSlug);
    const blocks = useMemo(() => getThemeBlocks(themeSlug), [themeSlug]);

    const [loading, setLoading] = useState(true);
    const [savedByTheme, setSavedByTheme] = useState({});
    const [content, setContent] = useState({});
    const [activeBlockKey, setActiveBlockKey] = useState(null);
    const [hoverAnchor, setHoverAnchor] = useState(null);
    const [resetTarget, setResetTarget] = useState(null);

    useEffect(() => {
        if(!editable){
            setLoading(false);
            return;
        }

        setActiveBlockKey(blocks[0]?.key ?? null);
        getContent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [themeSlug]);

    const getContent = () => {
        setLoading(true);

        api.get(`/site-texts`).then(res => {
            const saved = res.data.resource?.content ?? {};

            setSavedByTheme(saved);
            setContent(mergeThemeContent(themeSlug, saved[themeSlug] ?? null, saved[SHARED_KEY] ?? null));
        }).catch(error => {
            renderError(error);
            setContent(mergeThemeContent(themeSlug, null, null));
        }).then(() => setLoading(false));
    }

    const save = () => {
        if(!editable) return Promise.resolve();

        // Guarda por tema: se o cliente experimentar outro tema e voltar, os
        // textos que ele já tinha escrito continuam lá. O que é do site (os
        // links do menu) fica fora desse recorte.
        // O que é do site entra por cima do que já estava, bloco a bloco: um tema
        // que não oferece links (Saturno, Terra) devolve `shared` vazio, e
        // substituir a gaveta inteira apagaria os links do cliente.
        const { theme, shared } = diffThemeContent(themeSlug, content);
        const payload = {
            ...savedByTheme,
            [themeSlug]: theme,
            [SHARED_KEY]: { ...(savedByTheme[SHARED_KEY] ?? {}), ...shared }
        };

        return api.put(`/site-texts`, { content: payload }).then(res => {
            setSavedByTheme(res.data.resource?.content ?? payload);
        });
    }

    useImperativeHandle(ref, () => ({ save }));

    const activeBlock = blocks.find(block => block.key === activeBlockKey) ?? blocks[0] ?? null;

    const changeField = (blockKey, fieldKey, value) => {
        setContent(prev => ({
            ...prev,
            [blockKey]: { ...prev[blockKey], [fieldKey]: value }
        }));
    }

    const changeItem = (blockKey, repeaterKey, index, fieldKey, value) => {
        setContent(prev => {
            const items = [...(prev[blockKey]?.[repeaterKey] ?? [])];
            items[index] = { ...items[index], [fieldKey]: value };

            return { ...prev, [blockKey]: { ...prev[blockKey], [repeaterKey]: items } };
        });
    }

    const addItem = (blockKey, repeater) => {
        setContent(prev => ({
            ...prev,
            [blockKey]: {
                ...prev[blockKey],
                [repeater.key]: [...(prev[blockKey]?.[repeater.key] ?? []), getEmptyItem(repeater)]
            }
        }));
    }

    const removeItem = (blockKey, repeaterKey, index) => {
        setContent(prev => ({
            ...prev,
            [blockKey]: {
                ...prev[blockKey],
                [repeaterKey]: (prev[blockKey]?.[repeaterKey] ?? []).filter((_, i) => i !== index)
            }
        }));
    }

    /**
     * Ícone fora do catálogo sugerido viaja com o próprio desenho (`_node`):
     * os templates só têm compilados os sugeridos, e carregar a lucide inteira
     * na home do site custaria centenas de KB.
     */
    const changeIcon = (blockKey, repeaterKey, index, fieldKey, value, iconNode) => {
        setContent(prev => {
            const items = [...(prev[blockKey]?.[repeaterKey] ?? [])];
            const item = { ...items[index], [fieldKey]: value };

            if(iconNode) item[`${fieldKey}_node`] = iconNode;
            else delete item[`${fieldKey}_node`];

            items[index] = item;

            return { ...prev, [blockKey]: { ...prev[blockKey], [repeaterKey]: items } };
        });
    }

    const pick = (blockKey, fieldKey) => {
        setActiveBlockKey(blockKey);

        if(!fieldKey) return;

        window.setTimeout(() => {
            const target = document.querySelector(`#site-content [data-field="${blockKey}.${fieldKey}"] input, #site-content [data-field="${blockKey}.${fieldKey}"] textarea`);
            target?.focus();
        }, 60);
    }

    const confirmReset = () => {
        if(resetTarget === 'all'){
            setContent(blocks.reduce((values, block) => {
                values[block.key] = getBlockDefaults(block);
                return values;
            }, {}));
        } else if(resetTarget){
            const block = blocks.find(item => item.key === resetTarget);
            if(block) setContent(prev => ({ ...prev, [block.key]: getBlockDefaults(block) }));
        }

        setResetTarget(null);
        toast.success('Textos padrão restaurados. Salve para publicar.');
    }

    if(!themeSlug) return null;

    if(!editable){
        return (
            <section id="site-content">
                <div className="site-content-locked">
                    <Icon name="design" size={18} />
                    <div>
                        <strong>Este tema ainda não tem textos editáveis</strong>
                        <p>Escolha um dos temas Júpter, Mercúrio, Saturno ou Terra para editar as frases da home por aqui.</p>
                    </div>
                </div>
            </section>
        );
    }

    const renderToggle = (field, value, change, className = '') => {
        const on = field.onValue ?? '1';

        return (
            <div className={`sc-toggle-field ${className}`}>
                <label className="sc-toggle">
                    <input type="checkbox" checked={value === on} onChange={e => change(e.target.checked ? on : '')} />
                    <span className="sc-toggle-track"><span className="sc-toggle-knob" /></span>
                    <strong>{field.label}</strong>
                </label>
                {field.hint &&
                    <small>{field.hint}</small>
                }
            </div>
        );
    }

    const renderField = (blockKey, field) => {
        const value = content?.[blockKey]?.[field.key] ?? '';
        const isDefault = value === (field.default ?? '');

        if(field.type === 'choice'){
            return (
                <div
                    key={field.key}
                    className="sc-choice-field"
                    data-field={`${blockKey}.${field.key}`}
                    onMouseEnter={() => setHoverAnchor(field.anchor ?? null)}
                    onMouseLeave={() => setHoverAnchor(null)}
                >
                    <strong>{field.label}</strong>
                    <div className="sc-choice" role="radiogroup" aria-label={field.label}>
                        {field.options.map(option => (
                            <label key={option.value} className={`sc-choice-option ${value === option.value ? 'active' : ''}`}>
                                <input
                                    type="radio"
                                    name={`${blockKey}.${field.key}`}
                                    value={option.value}
                                    checked={value === option.value}
                                    onChange={() => changeField(blockKey, field.key, option.value)}
                                />
                                <span className="sc-choice-radio" />
                                <span>
                                    <b>{option.label}</b>
                                    {option.hint &&
                                        <small>{option.hint}</small>
                                    }
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            );
        }

        if(field.type === 'toggle'){
            return (
                <div
                    key={field.key}
                    data-field={`${blockKey}.${field.key}`}
                    onMouseEnter={() => setHoverAnchor(field.anchor ?? null)}
                    onMouseLeave={() => setHoverAnchor(null)}
                >
                    {renderToggle(field, value, newValue => changeField(blockKey, field.key, newValue))}
                </div>
            );
        }


        return (
            <div
                key={field.key}
                className="sc-field"
                data-field={`${blockKey}.${field.key}`}
                onMouseEnter={() => setHoverAnchor(field.anchor ?? null)}
                onMouseLeave={() => setHoverAnchor(null)}
                onFocus={() => setHoverAnchor(field.anchor ?? null)}
                onBlur={() => setHoverAnchor(null)}
            >
                {field.type === 'textarea' ?
                    <InputTextarea
                        label={field.label}
                        rows={field.rows ?? 2}
                        value={value}
                        change={newValue => changeField(blockKey, field.key, clamp(newValue, field.maxLength))}
                    />
                :
                    <Input
                        label={field.label}
                        value={value}
                        change={newValue => changeField(blockKey, field.key, clamp(newValue, field.maxLength))}
                    />
                }
                <div className="sc-field-foot">
                    <small>{field.hint ?? ''}</small>
                    {field.maxLength &&
                        <span className={value.length >= field.maxLength ? 'limit' : ''}>{value.length}/{field.maxLength}</span>
                    }
                </div>
                {!isDefault &&
                    <button type="button" className="sc-field-restore" onClick={() => changeField(blockKey, field.key, field.default ?? '')}>
                        <Icon name="refresh" size={11} /> voltar ao texto padrão
                    </button>
                }
            </div>
        );
    }

    const renderRepeater = block => {
        const repeater = block.repeater;
        const items = content?.[block.key]?.[repeater.key] ?? [];
        const hasIcon = repeater.fields.some(field => field.type === 'icon');

        return (
            <div className="sc-section">
                <div className="sc-section-head">
                    <strong>{repeater.title ?? repeater.itemName}</strong>
                    {repeater.hint &&
                        <small>{repeater.hint}</small>
                    }
                </div>

                <div className="sc-items">
                {items.map((item, index) => (
                    <div
                        key={index}
                        className="sc-item"
                        onMouseEnter={() => setHoverAnchor(block.anchor ?? null)}
                        onMouseLeave={() => setHoverAnchor(null)}
                    >
                        <div className="sc-item-head">
                            <span className="sc-item-number">{index + 1}</span>
                            <strong>{repeater.itemName} {index + 1}</strong>
                            {repeater.optional &&
                                <button type="button" className="sc-item-remove" onClick={() => removeItem(block.key, repeater.key, index)}>
                                    <Icon name="delete" size={12} /> remover
                                </button>
                            }
                        </div>
                        <div className={`sc-item-body ${hasIcon ? 'has-icon' : ''}`}>
                            {repeater.fields.map(field => {
                                if(field.type === 'icon'){
                                    return (
                                        <InputIcon
                                            key={field.key}
                                            label={field.label}
                                            value={item[field.key]}
                                            node={item[`${field.key}_node`]}
                                            change={(value, iconNode) => changeIcon(block.key, repeater.key, index, field.key, value, iconNode)}
                                        />
                                    );
                                }

                                const value = item[field.key] ?? '';

                                if(field.type === 'toggle'){
                                    return (
                                        <div key={field.key} className="sc-field">
                                            {renderToggle(
                                                field,
                                                item[field.key] ?? '',
                                                newValue => changeItem(block.key, repeater.key, index, field.key, newValue),
                                                'compact'
                                            )}
                                        </div>
                                    );
                                }

                                return (
                                    <div key={field.key} className="sc-field" data-field={`${block.key}.${repeater.key}.${index}.${field.key}`}>
                                        {field.type === 'textarea' ?
                                            <InputTextarea
                                                label={field.label}
                                                rows={field.rows ?? 2}
                                                value={value}
                                                change={newValue => changeItem(block.key, repeater.key, index, field.key, clamp(newValue, field.maxLength))}
                                            />
                                        :
                                            <Input
                                                label={field.label}
                                                value={value}
                                                change={newValue => changeItem(block.key, repeater.key, index, field.key, clamp(newValue, field.maxLength))}
                                            />
                                        }
                                        <div className="sc-field-foot">
                                            <small>{field.hint ?? ''}</small>
                                            {field.maxLength &&
                                                <span className={value.length >= field.maxLength ? 'limit' : ''}>{value.length}/{field.maxLength}</span>
                                            }
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}

                {repeater.optional && items.length === 0 &&
                    <p className="sc-items-empty">Nenhum link por enquanto.</p>
                }
                </div>

                {repeater.optional && items.length < (repeater.max ?? 20) &&
                    <button type="button" className="sc-items-add" onClick={() => addItem(block.key, repeater)}>
                        <Icon name="plus-circle" size={12} /> {repeater.addLabel ?? 'Acrescentar'}
                    </button>
                }
            </div>
        );
    }

    return (
        <section id="site-content">
            <div className="site-content-header">
                <div>
                    <h2 className="section-title">Textos e ícones do site</h2>
                    <p>Ajuste as frases da sua home e o que aparece na página do produto. Clique em um trecho da prévia à direita ou escolha um bloco abaixo — o que você mudar aparece na prévia na hora.</p>
                </div>
                <button type="button" className="site-content-reset-all" onClick={() => setResetTarget('all')}>
                    <Icon name="refresh" size={13} />
                    Restaurar todos os textos
                </button>
            </div>

            {loading &&
                <Loading />
            }

            {!loading &&
                <div className="site-content-layout">
                    <div className="site-content-editor">
                        <nav className="sc-tabs">
                            {blocks.map(block => {
                                const changes = countBlockChanges(block, content?.[block.key]);

                                return (
                                    <button
                                        key={block.key}
                                        type="button"
                                        className={`sc-tab ${block.key === activeBlock?.key ? 'active' : ''}`}
                                        onClick={() => setActiveBlockKey(block.key)}
                                    >
                                        <Icon name={block.icon} size={15} />
                                        {block.name}
                                        {changes > 0 &&
                                            <span className="sc-tab-badge" title={`${changes} texto(s) alterado(s)`}>{changes}</span>
                                        }
                                    </button>
                                );
                            })}
                        </nav>

                        {activeBlock &&
                            <div className="sc-panel">
                                <div className="sc-panel-head">
                                    <div>
                                        <h3>{activeBlock.name}</h3>
                                        <p>{activeBlock.hint}</p>
                                    </div>
                                    <button type="button" onClick={() => setResetTarget(activeBlock.key)}>
                                        <Icon name="refresh" size={12} />
                                        Restaurar padrão
                                    </button>
                                </div>

                                {(activeBlock.fields ?? []).length > 0 &&
                                    <div className="sc-fields">
                                        {activeBlock.fields.map(field => renderField(activeBlock.key, field))}
                                    </div>
                                }
                                {activeBlock.repeater && renderRepeater(activeBlock)}
                            </div>
                        }
                    </div>

                    <aside className="site-content-preview">
                        <div className="sc-preview-head">
                            <div>
                                <strong>{activeBlock?.page === 'product' ? 'Prévia da página do produto' : 'Prévia da home'}</strong>
                                <small>Tema {schema.name}</small>
                            </div>
                            <span className="sc-preview-tip">
                                <Icon name="edit" size={11} />
                                clique para editar
                            </span>
                        </div>
                        <div className="sc-preview-frame">
                            {activeBlock?.page === 'product' ?
                                <ProductPreview
                                    block={activeBlock}
                                    content={content}
                                    hoverAnchor={hoverAnchor}
                                    pick={pick}
                                />
                            :
                                <HomePreview
                                    slug={themeSlug}
                                    content={content}
                                    activeBlock={activeBlock?.key}
                                    hoverAnchor={hoverAnchor}
                                    pick={pick}
                                />
                            }
                        </div>
                        <p className="sc-preview-legend">
                            Desenho simplificado da página. Cores, imagens e produtos reais continuam vindo do seu tema, banners e catálogo.
                        </p>
                    </aside>
                </div>
            }

            <Modal
                title="Restaurar textos padrão"
                show={resetTarget !== null}
                close={() => setResetTarget(null)}
                footer={
                    <>
                        <Button type="secondary" size="small" text="Cancelar" action={() => setResetTarget(null)} />
                        <Button type="primary" size="small" svg="refresh" text="Restaurar" action={confirmReset} />
                    </>
                }
            >
                <p>
                    {resetTarget === 'all' ?
                        'Todos os textos, ícones e opções do site voltam para o padrão do tema. Nada é publicado até você clicar em “Salvar alterações”.'
                    :
                        'Este bloco volta para o padrão do tema. Nada é publicado até você clicar em “Salvar alterações”.'
                    }
                </p>
            </Modal>
        </section>
    );
});

export default SiteContent;
